export interface KitchenOwner {
  user_id?: number;
  name: string;
  email: string | null;
  phone: string | null;
  profile_photo: string | null;
  is_active: boolean;
  is_blocked: boolean;
  blocked_reason: string | null;
  last_active: string | null;
  created_at: string | null;
}

export interface NormalizedKitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo: string | null;
  kitchen_type: string;
  kitchen_category: string;
  city: string;
  state: string;
  country: string;
  pincode: string | null;
  kitchen_address: string;
  latitude: number | null;
  longitude: number | null;
  contact_number: string;
  contact_email: string | null;
  made_by: string;
  created_at: string;
  created_by: string;
  device_token: string | null;
  user_id: number | null;
  delivery_radius_km: number;
  delivery_fee: number;
  minimum_order_amount: number;
  average_preparation_time: number;
  opening_time: string | null;
  closing_time: string | null;
  average_rating: number;
  total_reviews: number;
  total_orders: number;
  total_earnings: number;
  rejection_reason: string | null;
  photo_id_url: string | null;
  photo_id_type: string | null;
  training_certificate_url: string | null;
  document_verification_status: string;
  document_rejection_reason: string | null;
  document_verified_at: string | null;
  document_verified_by: string | null;
  status: boolean;
  is_approved: boolean;
  approval_status: string;
  is_online: boolean;
  is_accepting_orders: boolean;
  accepting_orders: boolean;
  is_blocked: boolean;
  owner: KitchenOwner | null;
  owner_name: string;
  owner_email: string | null;
  owner_phone: string | null;
  owner_is_active: boolean;
  owner_is_blocked: boolean;
  owner_blocked_reason: string | null;
  owner_last_active: string | null;
  owner_profile_photo: string | null;
}

export interface NormalizedKitchensResponse {
  success: boolean;
  count: number;
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
  kitchens: NormalizedKitchen[];
}

interface RawOwner {
  user_id?: number;
  name?: string;
  email?: string;
  phone?: string;
  profile_photo?: string;
  is_active?: boolean;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  last_active?: string | null;
  created_at?: string | null;
}

interface RawKitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo?: string | null;
  kitchen_type?: string;
  kitchen_category?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string | null;
  kitchen_address?: string;
  latitude?: number | null;
  longitude?: number | null;
  contact_number?: string;
  contact_email?: string | null;
  made_by?: string;
  created_at?: string;
  created_by?: string;
  device_token?: string | null;
  user_id?: number | null;
  delivery_radius_km?: number;
  delivery_fee?: number;
  minimum_order_amount?: number;
  average_preparation_time?: number;
  opening_time?: string | null;
  closing_time?: string | null;
  average_rating?: number;
  total_reviews?: number;
  total_orders?: number;
  total_earnings?: number;
  rejection_reason?: string | null;
  photo_id_url?: string | null;
  photo_id_type?: string | null;
  training_certificate_url?: string | null;
  document_verification_status?: string;
  document_rejection_reason?: string | null;
  document_verified_at?: string | null;
  document_verified_by?: string | null;
  status?: boolean;
  is_approved?: boolean;
  approval_status?: string;
  is_online?: boolean;
  kitchen_online_status?: boolean;
  is_accepting_orders?: boolean;
  accepting_orders?: boolean;
  is_kitchen_blocked?: boolean;
  owner?: RawOwner | null;
}

export const normalizeKitchen = (raw: RawKitchen): NormalizedKitchen => {
  const isApproved = raw.is_approved ?? (raw.approval_status === 'approved');
  const owner = raw.owner ?? null;

  return {
    kitchen_id: raw.kitchen_id,
    kitchen_name: raw.kitchen_name ?? '',
    kitchen_photo: raw.kitchen_photo ?? null,
    kitchen_type: raw.kitchen_type ?? '',
    kitchen_category: raw.kitchen_category ?? '',
    city: raw.city ?? '',
    state: raw.state ?? '',
    country: raw.country ?? '',
    pincode: raw.pincode ?? null,
    kitchen_address: raw.kitchen_address ?? '',
    latitude: raw.latitude ?? null,
    longitude: raw.longitude ?? null,
    contact_number: raw.contact_number ?? '',
    contact_email: raw.contact_email ?? null,
    made_by: raw.made_by ?? '',
    created_at: raw.created_at ?? '',
    created_by: raw.created_by ?? '',
    device_token: raw.device_token ?? null,
    user_id: raw.user_id ?? null,
    delivery_radius_km: raw.delivery_radius_km ?? 15,
    delivery_fee: raw.delivery_fee ?? 30,
    minimum_order_amount: raw.minimum_order_amount ?? 0,
    average_preparation_time: raw.average_preparation_time ?? 30,
    opening_time: raw.opening_time ?? null,
    closing_time: raw.closing_time ?? null,
    average_rating: raw.average_rating ?? 0,
    total_reviews: raw.total_reviews ?? 0,
    total_orders: raw.total_orders ?? 0,
    total_earnings: raw.total_earnings ?? 0,
    rejection_reason: raw.rejection_reason ?? null,
    photo_id_url: raw.photo_id_url ?? null,
    photo_id_type: raw.photo_id_type ?? null,
    training_certificate_url: raw.training_certificate_url ?? null,
    document_verification_status: raw.document_verification_status ?? 'pending',
    document_rejection_reason: raw.document_rejection_reason ?? null,
    document_verified_at: raw.document_verified_at ?? null,
    document_verified_by: raw.document_verified_by ?? null,
    status: raw.status ?? false,
    is_approved: isApproved,
    approval_status: raw.approval_status ?? (isApproved ? 'approved' : 'pending'),
    is_online: raw.kitchen_online_status ?? raw.is_online ?? false,
    is_accepting_orders: raw.is_accepting_orders ?? raw.accepting_orders ?? false,
    accepting_orders: raw.accepting_orders ?? false,
    is_blocked: raw.is_kitchen_blocked ?? (raw.status === false) ?? false,
    owner,
    owner_name: owner?.name ?? raw.made_by ?? '—',
    owner_email: owner?.email ?? raw.contact_email ?? raw.created_by ?? null,
    owner_phone: owner?.phone ?? raw.contact_number ?? null,
    owner_is_active: owner?.is_active ?? true,
    owner_is_blocked: owner?.is_blocked ?? false,
    owner_blocked_reason: owner?.blocked_reason ?? null,
    owner_last_active: owner?.last_active ?? null,
    owner_profile_photo: owner?.profile_photo ?? null,
  };
};

export const normalizeKitchensResponse = (raw: any): NormalizedKitchensResponse => ({
  success: raw.success ?? true,
  count: raw.count ?? (raw.kitchens?.length ?? 0),
  total: raw.total ?? (raw.kitchens?.length ?? 0),
  page: raw.page ?? 1,
  limit: raw.limit ?? 20,
  has_next: raw.has_next ?? false,
  has_prev: raw.has_prev ?? false,
  kitchens: (raw.kitchens || []).map(normalizeKitchen),
});
